import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API, SHOW_PRICING } from '../App';
import { DashboardLayout } from '../components/DashboardLayout';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Calendar, Grid3X3, Palette, Plus, ChevronLeft, ChevronRight,
  Lock, Image as ImageIcon, Video, Circle, Images, X, Check, Sparkles
} from 'lucide-react';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const ContentPlanningStudio = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('calendar');
  const [pillars, setPillars] = useState([]);
  const [contentTypes, setContentTypes] = useState([]);
  const [plan, setPlan] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [showAddModal, setShowAddModal] = useState(false);
  const [newItem, setNewItem] = useState({
    date: '', content_type: 'post', pillar: 'inspire', title: '', description: '', status: 'planned'
  });
  const [moodboards, setMoodboards] = useState([]);
  const [feedPreview, setFeedPreview] = useState([]);

  const tier = user?.subscription_tier || 'free';
  const subscriptionStatus = user?.subscription_status || 'inactive';
  const isBetaUser = user?.is_beta_user || user?.beta_access || subscriptionStatus === 'beta';
  const hasAccess = !SHOW_PRICING || tier === 'premium' || tier === 'pro' || tier === 'experience' || isBetaUser;

  useEffect(() => {
    if (hasAccess) {
      fetchData();
    }
  }, [hasAccess]);

  const fetchData = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const [pillarsRes, planRes, moodboardsRes] = await Promise.all([
        axios.get(`${API}/content-studio/pillars`),
        axios.get(`${API}/content-studio/plan`, { headers }),
        axios.get(`${API}/content-studio/moodboards`, { headers })
      ]);
      setPillars(pillarsRes.data.pillars);
      setContentTypes(pillarsRes.data.content_types);
      if (planRes.data.plan) {
        setPlan(planRes.data.plan);
        setFeedPreview(planRes.data.plan.items?.filter(i => i.status === 'scheduled') || []);
      }
      setMoodboards(moodboardsRes.data.moodboards);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const getContentIcon = (type) => {
    switch (type) {
      case 'post': return <ImageIcon size={14} />;
      case 'carousel': return <Images size={14} />;
      case 'reel': return <Video size={14} />;
      case 'story': return <Circle size={14} />;
      default: return <ImageIcon size={14} />;
    }
  };

  const getPillarColor = (pillarId) => {
    const pillar = pillars.find(p => p.id === pillarId);
    return pillar?.color || '#D4AF37';
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = (firstDay.getDay() + 6) % 7; // Monday = 0
    
    const days = [];
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getItemsForDate = (date) => {
    if (!date || !plan?.items) return [];
    const dateStr = date.toISOString().split('T')[0];
    return plan.items.filter(item => item.date === dateStr);
  };

  const handleAddItem = async () => {
    if (!newItem.title || !newItem.date) {
      toast.error('Please fill in title and date');
      return;
    }

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const updatedItems = [...(plan?.items || []), newItem];
      
      if (plan?.plan_id) {
        await axios.put(`${API}/content-studio/plan/${plan.plan_id}`, { items: updatedItems }, { headers });
      } else {
        const response = await axios.post(`${API}/content-studio/plan`, { items: updatedItems }, { headers });
        setPlan(response.data);
      }
      
      setPlan(prev => ({ ...prev, items: updatedItems }));
      setShowAddModal(false);
      setNewItem({ date: '', content_type: 'post', pillar: 'inspire', title: '', description: '', status: 'planned' });
      toast.success('Content added!');
    } catch (error) {
      toast.error('Failed to add content');
    }
  };

  if (!hasAccess) {
    return (
      <DashboardLayout title="Content Planning Studio">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="card-gold p-16 text-center">
            <Lock className="text-[#D4AF37] mx-auto mb-6" size={60} />
            <h2 className="font-serif text-3xl text-white mb-4">Premium Feature</h2>
            <p className="text-[#A1A1AA] mb-8 max-w-md mx-auto">
              Content Planning Studio includes calendar, moodboard creator, and feed preview.
              Upgrade to Premium or Pro to access.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="btn-gold px-10 py-4" data-testid="upgrade-cps-btn">
              Upgrade Now
            </Button>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Content Planning Studio">
      <div className="max-w-7xl mx-auto">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-[#1C1C1C] pb-4">
          {[
            { id: 'calendar', label: 'Calendar', icon: Calendar },
            { id: 'moodboard', label: 'Moodboard', icon: Palette },
            { id: 'feed', label: 'Feed Preview', icon: Grid3X3 }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 flex items-center gap-2 transition-all ${
                  activeTab === tab.id
                    ? 'text-[#E5C585] bg-[#E5C585]/5 border-b-2 border-[#D4AF37]'
                    : 'text-[#71717A] hover:text-white'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon size={18} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Calendar View */}
        {activeTab === 'calendar' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="card-premium p-6 mb-6">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                  <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))} className="text-[#71717A] hover:text-white">
                    <ChevronLeft size={24} />
                  </button>
                  <h3 className="font-serif text-xl text-white">
                    {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                  </h3>
                  <button onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))} className="text-[#71717A] hover:text-white">
                    <ChevronRight size={24} />
                  </button>
                </div>
                <Button onClick={() => setShowAddModal(true)} className="btn-gold px-4 py-2 flex items-center gap-2" data-testid="add-content-btn">
                  <Plus size={16} />
                  Add Content
                </Button>
              </div>

              {/* Calendar Grid */}
              <div className="grid grid-cols-7 gap-1">
                {DAYS.map(day => (
                  <div key={day} className="text-center text-[#52525B] text-xs font-accent tracking-wider py-2">
                    {day}
                  </div>
                ))}
                {getDaysInMonth(currentMonth).map((date, index) => {
                  const items = getItemsForDate(date);
                  const isToday = date && date.toDateString() === new Date().toDateString();
                  
                  return (
                    <div
                      key={index}
                      className={`min-h-[100px] p-2 border border-[#1C1C1C] ${
                        date ? 'hover:border-[#D4AF37]/30 cursor-pointer' : ''
                      } ${isToday ? 'bg-[#D4AF37]/5' : ''}`}
                      onClick={() => date && setSelectedDate(date)}
                    >
                      {date && (
                        <>
                          <span className={`text-sm ${isToday ? 'text-[#D4AF37]' : 'text-[#71717A]'}`}>
                            {date.getDate()}
                          </span>
                          <div className="mt-1 space-y-1">
                            {items.slice(0, 2).map((item, i) => (
                              <div
                                key={i}
                                className="text-[10px] px-1.5 py-0.5 truncate flex items-center gap-1"
                                style={{ backgroundColor: `${getPillarColor(item.pillar)}20`, color: getPillarColor(item.pillar) }}
                              >
                                {getContentIcon(item.content_type)}
                                {item.title}
                              </div>
                            ))}
                            {items.length > 2 && (
                              <span className="text-[10px] text-[#52525B]">+{items.length - 2} more</span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Content Pillars Legend */}
            <div className="card-luxury p-4 flex items-center gap-6 flex-wrap">
              <span className="text-[#52525B] text-xs font-accent tracking-wider">PILLARS:</span>
              {pillars.map(pillar => (
                <div key={pillar.id} className="flex items-center gap-2">
                  <div className="w-3 h-3" style={{ backgroundColor: pillar.color }} />
                  <span className="text-[#A1A1AA] text-sm">{pillar.name}</span>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Moodboard View */}
        {activeTab === 'moodboard' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="card-premium p-8 text-center">
              <Palette className="text-[#27272A] mx-auto mb-4" size={60} />
              <h3 className="font-serif text-xl text-white mb-2">Moodboard Creator</h3>
              <p className="text-[#52525B] mb-6">Create visual direction boards for your brand content.</p>
              <Button className="btn-outline-gold px-6 py-3" data-testid="create-moodboard-btn">
                <Plus size={16} className="mr-2" />
                Create Moodboard
              </Button>
              
              {moodboards.length > 0 && (
                <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
                  {moodboards.map(board => (
                    <div key={board.moodboard_id} className="card-luxury p-4 text-left">
                      <h4 className="text-white text-sm mb-2">{board.name}</h4>
                      <p className="text-[#52525B] text-xs">{board.items?.length || 0} images</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Feed Preview View */}
        {activeTab === 'feed' && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <div className="card-premium p-8">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="font-serif text-xl text-white mb-1">Instagram Feed Preview</h3>
                  <p className="text-[#52525B] text-sm">Visualize how your content will look on your feed.</p>
                </div>
              </div>

              {/* Mock Feed Grid */}
              <div className="max-w-md mx-auto">
                <div className="grid grid-cols-3 gap-1">
                  {[...Array(9)].map((_, i) => {
                    const item = feedPreview[i];
                    return (
                      <div key={i} className="aspect-square bg-[#1C1C1C] border border-[#27272A] flex items-center justify-center relative group">
                        {item ? (
                          <>
                            <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: `${getPillarColor(item.pillar)}10` }}>
                              {getContentIcon(item.content_type)}
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 p-2 bg-[#050505]/80 opacity-0 group-hover:opacity-100 transition-opacity">
                              <p className="text-[10px] text-white truncate">{item.title}</p>
                            </div>
                          </>
                        ) : (
                          <Plus className="text-[#27272A]" size={20} />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <p className="text-center text-[#52525B] text-sm mt-6">
                Add scheduled content to see it in your feed preview
              </p>
            </div>
          </motion.div>
        )}

        {/* Add Content Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card-luxury p-8 max-w-lg w-full"
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-serif text-xl text-white">Add Content</h3>
                <button onClick={() => setShowAddModal(false)} className="text-[#52525B] hover:text-white">
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Date</label>
                  <Input
                    type="date"
                    value={newItem.date}
                    onChange={(e) => setNewItem({ ...newItem, date: e.target.value })}
                    className="input-underline"
                  />
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Content Type</label>
                  <div className="flex gap-2 flex-wrap">
                    {contentTypes.map(type => (
                      <button
                        key={type.id}
                        onClick={() => setNewItem({ ...newItem, content_type: type.id })}
                        className={`px-4 py-2 text-sm flex items-center gap-2 transition-all ${
                          newItem.content_type === type.id
                            ? 'bg-[#D4AF37]/20 border border-[#D4AF37] text-[#E5C585]'
                            : 'bg-[#0A0A0A] border border-[#27272A] text-[#A1A1AA]'
                        }`}
                      >
                        {getContentIcon(type.id)}
                        {type.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Content Pillar</label>
                  <div className="flex gap-2 flex-wrap">
                    {pillars.map(pillar => (
                      <button
                        key={pillar.id}
                        onClick={() => setNewItem({ ...newItem, pillar: pillar.id })}
                        className={`px-4 py-2 text-sm transition-all ${
                          newItem.pillar === pillar.id
                            ? 'border text-white'
                            : 'bg-[#0A0A0A] border border-[#27272A] text-[#A1A1AA]'
                        }`}
                        style={newItem.pillar === pillar.id ? { borderColor: pillar.color, backgroundColor: `${pillar.color}20` } : {}}
                      >
                        {pillar.name}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Title</label>
                  <Input
                    value={newItem.title}
                    onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                    placeholder="Content title"
                    className="input-underline"
                  />
                </div>

                <div>
                  <label className="text-[#A1A1AA] text-sm mb-2 block">Description (Optional)</label>
                  <Textarea
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    placeholder="Brief description..."
                    className="bg-transparent border border-[#27272A] focus:border-[#D4AF37] rounded-none p-3 text-white min-h-[80px]"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button onClick={() => setShowAddModal(false)} className="btn-outline-gold flex-1 py-3">
                    Cancel
                  </Button>
                  <Button onClick={handleAddItem} className="btn-gold flex-1 py-3">
                    Add Content
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ContentPlanningStudio;
